import { useEffect, useRef, useState } from "react";

/**
 * Network stability monitor
 * - NO backend dependency (avoids cold start confusion)
 * - Uses ping to static endpoint + online/offline events
 */
export default function useNetworkStability(options = {}) {
  const {
    //  Prefer to host a static file on your frontend hosting
    // Example: https://your-frontend-domain.com/ping.txt
    pingUrl = `${window.location.origin}/ping.txt`,
    intervalMs = 6000,
    timeoutMs = 6000,
    sampleWindow = 6, // last N pings
  } = options;

  const [netStatus, setNetStatus] = useState("checking"); // checking | stable | warning | unstable | offline
  const [latencyMs, setLatencyMs] = useState(null);
  const [lossPct, setLossPct] = useState(0);

  const latenciesRef = useRef([]); // store last N latencies
  const lossRef = useRef([]); // store last N success/fail

  const ping = async () => {
    if (!navigator.onLine) {
      setNetStatus("offline");
      setLatencyMs(null);
      setLossPct(100);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const start = performance.now();
    try {
      // HEAD is lighter but some hosts block it; if issue, switch to GET
      await fetch(pingUrl, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
        mode: "no-cors", // allow google generate_204 without CORS issues
      });

      const end = performance.now();
      clearTimeout(timeoutId);

      const ms = Math.round(end - start);

      // store samples
      latenciesRef.current.push(ms);
      lossRef.current.push(1);

      if (latenciesRef.current.length > sampleWindow)
        latenciesRef.current.shift();

      if (lossRef.current.length > sampleWindow) lossRef.current.shift();

      // compute avg latency + loss %
      const latencies = latenciesRef.current;
      const avgLatency =
        latencies.reduce((a, b) => a + b, 0) / latencies.length;

      // jitter = average abs difference between consecutive pings
      let jitter = 0;
      if (latencies.length >= 2) {
        let sum = 0;
        for (let i = 1; i < latencies.length; i++) {
          sum += Math.abs(latencies[i] - latencies[i - 1]);
        }
        jitter = sum / (latencies.length - 1);
      }

      const fails = lossRef.current.filter((x) => x === 0).length;
      const loss = Math.round((fails / lossRef.current.length) * 100);

      setLatencyMs(Math.round(avgLatency));
      setLossPct(loss);

      // classify
      //  Tune these thresholds based on your needs
      if (loss >= 30 || avgLatency > 1200) {
        setNetStatus("unstable");
      } else if (avgLatency > 400 || jitter > 250 || loss >= 10) {
        setNetStatus("warning");
      } else {
        setNetStatus("stable");
      }
    } catch (e) {
      clearTimeout(timeoutId);

      latenciesRef.current.push(timeoutMs);
      lossRef.current.push(0);

      if (latenciesRef.current.length > sampleWindow)
        latenciesRef.current.shift();
      if (lossRef.current.length > sampleWindow) lossRef.current.shift();

      const fails = lossRef.current.filter((x) => x === 0).length;
      const loss = Math.round((fails / lossRef.current.length) * 100);
      setLossPct(loss);

      // if browser says online but ping fails -> unstable
      setNetStatus(navigator.onLine ? "unstable" : "offline");
    }
  };

  useEffect(() => {
    // initial
    ping();

    const id = setInterval(ping, intervalMs);

    const goOnline = () => {
      latenciesRef.current = [];
      lossRef.current = [];
      ping();
    };
    const goOffline = () => {
      setNetStatus("offline");
      setLatencyMs(null);
      setLossPct(100);
    };

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      clearInterval(id);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { netStatus, latencyMs, lossPct };
}
